import { useEffect, useState } from 'react';

import { Alert, Badge, Button, Container, Spinner, Table } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa6';
import { useApp } from 'common/AppContext';

import { unwrap } from './helpers';

export const RunsByProject = () => {

    const { api } = useApp();
    const { projectId } = useParams();
    const history = useHistory();

    const [project, setProject] = useState(null);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {

        if (!projectId) return;

        setLoading(true);
        setError(null);

        Promise.all([
            api.get(`/api/testrail/projects/${projectId}`),
            api.get(`/api/testrail/projects/${projectId}/runs`)
        ])
            .then(([projectData, runsData]) => {

                setProject(projectData);
                setRuns(unwrap(runsData, 'runs'));

            })
            .catch(e => setError(e?.err || e?.message || String(e)))
            .finally(() => setLoading(false));

    }, [api, projectId]);

    const totalTests = r => (
        (r.passed_count || 0) + (r.failed_count || 0) + (r.untested_count || 0)
        + (r.blocked_count || 0) + (r.retest_count || 0) + (r.custom_status1_count || 0)
    );

    return (

        <Container className="py-4">

            <Button className="mb-3" onClick={() => history.push('/testrail/runs')} variant="outline-secondary">
                <FaArrowLeft className="me-2" />
                Back to projects
            </Button>

            {project && (
                <div className="mb-3">
                    <div className="text-muted small">
                        P
                        {project.id}
                    </div>
                    <h3 className="mb-0">
                        {project.name}
                    </h3>
                    <p className="text-muted small mb-0">
                        Open test runs (not yet closed).
                    </p>
                </div>
            )}

            {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
            )}

            {loading && <Spinner animation="border" />}

            {!loading && runs.length > 0 && (
                <Table hover striped size="sm">
                    <thead>
                        <tr>
                            <th>
                                ID
                            </th>
                            <th>
                                Name
                            </th>
                            <th>
                                Created
                            </th>
                            <th className="text-end">
                                Tests
                            </th>
                            <th className="text-end">
                                Passed
                            </th>
                            <th className="text-end">
                                Failed
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map(r => (
                            <tr
                                key={r.id}
                                onClick={() => history.push(`/testrail/runs/${r.id}`)}
                                style={{ cursor: 'pointer' }}
                            >
                                <td>
                                    {r.id}
                                </td>
                                <td>
                                    {r.name}
                                </td>
                                <td>
                                    {r.created_on ? new Date(r.created_on * 1000).toLocaleDateString() : '—'}
                                </td>
                                <td className="text-end">
                                    {totalTests(r)}
                                </td>
                                <td className="text-end">
                                    {r.passed_count || 0}
                                </td>
                                <td className="text-end">
                                    {r.failed_count || 0}
                                </td>
                                <td onClick={e => e.stopPropagation()}>
                                    {r.url && (
                                        <a href={r.url} rel="noreferrer" target="_blank">
                                            TestRail
                                        </a>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {!loading && runs.length === 0 && !error && (
                <Alert variant="light">
                    No open runs for this project.
                </Alert>
            )}

            {!loading && runs.length === 0 && (
                <div className="text-muted small mt-2">
                    <Badge bg="secondary">
                        tip
                    </Badge>
                    {' '}
                    Closed runs aren&apos;t shown here. Open them in TestRail directly to see history.
                </div>
            )}

        </Container>

    );

};
